import React from 'react';
import { Button, message } from 'antd';
import { FileExcelOutlined } from '@ant-design/icons';
import * as XLSX from 'xlsx';
import moment from 'moment';

const CENTROS_PRODUCCION = {
  1: 'Buenos Aires',
  2: 'Santa Fe', 
  3: 'Córdoba',
  4: 'Entre Ríos',
  5: 'La Pampa'
};

const MESES = {
  1: 'ENERO', 2: 'FEBRERO', 3: 'MARZO', 4: 'ABRIL',
  5: 'MAYO', 6: 'JUNIO', 7: 'JULIO', 8: 'AGOSTO',
  9: 'SEPTIEMBRE', 10: 'OCTUBRE', 11: 'NOVIEMBRE', 12: 'DICIEMBRE'
};

const ExportPlanMensualExcel = ({ planData, calendarioData }) => {
  
  const exportToExcel = () => {
    try {
      if (!planData || !calendarioData) {
        message.error('No hay datos para exportar');
        return;
      }

      const { plan, calendario } = calendarioData;
      const diasDelMes = Object.keys(calendario).length;
      
      // Crear el libro de Excel
      const workbook = XLSX.utils.book_new();

      // Preparar datos para la hoja principal similar al Excel mostrado
      const worksheetData = [];
      
      // Encabezado principal
      worksheetData.push([
        '', '', '', '', '', '', '', 'PLAN DE TRABAJO MENSUAL', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', ''
      ]);
      worksheetData.push([
        '', '', '', '', '', '', '', `CENTRO DE PRODUCCIÓN ${CENTROS_PRODUCCION[plan.id_cp] || plan.id_cp}`, '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', ''
      ]);
      worksheetData.push([]); // Línea vacía

      // Encabezado de la tabla
      const headerRow = [
        'MES', 'Actividad/Día del mes'
      ];
      
      // Agregar días del mes como columnas
      for (let dia = 1; dia <= diasDelMes; dia++) {
        headerRow.push(dia.toString());
      }
      headerRow.push('TOTAL JORNALES', 'META (en % o #)');
      
      // Agregar días del siguiente mes si es necesario
      const siguienteMes = plan.mes === 12 ? 1 : plan.mes + 1;
      const siguienteAño = plan.mes === 12 ? plan.año + 1 : plan.año;
      const diasSiguienteMes = moment(`${siguienteAño}-${siguienteMes.toString().padStart(2, '0')}-01`).daysInMonth();
      
      for (let dia = 1; dia <= Math.min(diasSiguienteMes, 10); dia++) {
        headerRow.push(dia.toString());
      }
      headerRow.push('TOTAL JORNALES', 'META (en % o #)');

      worksheetData.push(headerRow);

      // Organizar actividades por tipo
      const actividadesPorTipo = {};
      
      Object.values(calendario).forEach(diaData => {
        diaData.actividades.forEach(actividad => {
          const actividadNombre = actividad.actividad?.nombre || 'Actividad desconocida';
          
          if (!actividadesPorTipo[actividadNombre]) {
            actividadesPorTipo[actividadNombre] = {
              nombre: actividadNombre,
              dias: {},
              totalPlanificado: 0,
              totalEjecutado: 0,
              meta: actividad.meta_mensual || 0
            };
          }
          
          actividadesPorTipo[actividadNombre].dias[diaData.dia] = {
            planificado: actividad.jornales_planificados,
            ejecutado: actividad.jornales_ejecutados
          };
          
          actividadesPorTipo[actividadNombre].totalPlanificado += actividad.jornales_planificados;
          actividadesPorTipo[actividadNombre].totalEjecutado += actividad.jornales_ejecutados;
        });
      });

      // Agregar filas de actividades
      Object.values(actividadesPorTipo).forEach(actividad => {
        const row = [
          MESES[plan.mes], // Mes
          actividad.nombre  // Nombre de la actividad
        ];
        
        // Agregar jornales por día
        for (let dia = 1; dia <= diasDelMes; dia++) {
          const diaData = actividad.dias[dia];
          if (diaData) {
            // Mostrar ejecutado si existe, sino planificado
            row.push(diaData.ejecutado > 0 ? diaData.ejecutado : diaData.planificado || '');
          } else {
            row.push('');
          }
        }
        
        // Total jornales y meta del primer mes
        row.push(actividad.totalEjecutado > 0 ? actividad.totalEjecutado : actividad.totalPlanificado);
        row.push(actividad.meta || '');
        
        // Días del siguiente mes (vacíos por ahora)
        for (let dia = 1; dia <= Math.min(diasSiguienteMes, 10); dia++) {
          row.push('');
        }
        
        // Total y meta del siguiente mes
        row.push(0);
        row.push('');
        
        worksheetData.push(row);
      });

      // Agregar sección de resumen de operarios si existe
      worksheetData.push([]); // Línea vacía
      worksheetData.push(['Total mano de obra', '', ...Array(diasDelMes).fill(''), '', '', ...Array(Math.min(diasSiguienteMes, 10)).fill(''), '', '']);
      
      // Sección de operarios
      worksheetData.push(['Operarios', '', ...Array(diasDelMes).fill(''), '', '', ...Array(Math.min(diasSiguienteMes, 10)).fill(''), '', '']);
      worksheetData.push(['Total operarios en campo', '', ...Array(diasDelMes).fill(''), '', '', ...Array(Math.min(diasSiguienteMes, 10)).fill(''), '', '']);
      worksheetData.push(['Incapacitados', '', ...Array(diasDelMes).fill(''), '', '', ...Array(Math.min(diasSiguienteMes, 10)).fill(''), '', '']);
      worksheetData.push(['Permiso', '', ...Array(diasDelMes).fill(''), '', '', ...Array(Math.min(diasSiguienteMes, 10)).fill(''), '', '']);
      worksheetData.push(['Renuncias', '', ...Array(diasDelMes).fill(''), '', '', ...Array(Math.min(diasSiguienteMes, 10)).fill(''), '', '']);
      worksheetData.push(['En vacaciones', '', ...Array(diasDelMes).fill(''), '', '', ...Array(Math.min(diasSiguienteMes, 10)).fill(''), '', '']);
      
      // Sección de contratistas
      worksheetData.push(['Contratistas', '', ...Array(diasDelMes).fill(''), '', '', ...Array(Math.min(diasSiguienteMes, 10)).fill(''), '', '']);
      worksheetData.push(['Aplicación de herbicida por parejo (litros - Canecas/ha)', '', ...Array(diasDelMes).fill(''), '', '', ...Array(Math.min(diasSiguienteMes, 10)).fill(''), '', '']);
      worksheetData.push(['Total contratistas', '', ...Array(diasDelMes).fill(''), '', '', ...Array(Math.min(diasSiguienteMes, 10)).fill(''), '', '']);

      // Agregar fila de observaciones al final
      worksheetData.push([]);
      worksheetData.push(['Observaciones', planData.observaciones_generales || '', ...Array(headerRow.length - 2).fill('')]);

      // Crear la hoja de trabajo
      const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

      // Configurar el ancho de las columnas
      const columnWidths = [
        { wch: 15 }, // MES
        { wch: 40 }, // Actividad
        ...Array(diasDelMes).fill({ wch: 4 }), // Días del mes
        { wch: 12 }, // Total jornales
        { wch: 12 }, // Meta
        ...Array(Math.min(diasSiguienteMes, 10)).fill({ wch: 4 }), // Días siguiente mes
        { wch: 12 }, // Total jornales siguiente
        { wch: 12 }  // Meta siguiente
      ];
      worksheet['!cols'] = columnWidths;

      // Configurar merge de celdas para el título
      const merges = [
        { s: { r: 0, c: 7 }, e: { r: 0, c: 20 } }, // Título principal
        { s: { r: 1, c: 7 }, e: { r: 1, c: 20 } }  // Subtítulo centro
      ];
      worksheet['!merges'] = merges;

      // Aplicar estilos básicos (colores de fondo)
      const range = XLSX.utils.decode_range(worksheet['!ref']);
      
      // Estilo para el encabezado
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const headerCell = worksheet[XLSX.utils.encode_cell({ r: 3, c: C })];
        if (headerCell) {
          headerCell.s = {
            fill: { fgColor: { rgb: "92D050" } }, // Verde claro
            font: { bold: true },
            alignment: { horizontal: "center" }
          };
        }
      }

      // Agregar la hoja al libro
      XLSX.utils.book_append_sheet(workbook, worksheet, `Plan ${MESES[plan.mes]} ${plan.año}`);

      // Crear hoja de estadísticas adicional
      const statsData = [
        ['ESTADÍSTICAS DEL PLAN MENSUAL'],
        [''],
        ['Plan:', plan.titulo],
        ['Centro de Producción:', CENTROS_PRODUCCION[plan.id_cp]],
        ['Mes:', MESES[plan.mes]],
        ['Año:', plan.año],
        ['Estado:', plan.estado],
        ['Responsable:', `${planData.responsableCentro?.nombre || ''} ${planData.responsableCentro?.apellido || ''}`],
        ['Fecha de Creación:', moment(planData.fecha_creacion).format('DD/MM/YYYY')],
        [''],
        ['RESUMEN DE JORNALES'],
        ['Total Planificados:', planData.total_jornales_planificados || 0],
        ['Total Ejecutados:', planData.total_jornales_ejecutados || 0],
        ['Porcentaje de Cumplimiento:', 
          planData.total_jornales_planificados > 0 
            ? `${Math.round((planData.total_jornales_ejecutados / planData.total_jornales_planificados) * 100)}%`
            : '0%'
        ],
        [''],
        ['ACTIVIDADES POR DÍA'],
        ['Día', 'Total Planificado', 'Total Ejecutado', 'Actividades']
      ];

      // Agregar estadísticas por día
      Object.values(calendario).forEach(diaData => {
        statsData.push([
          diaData.dia,
          diaData.total_jornales_planificados,
          diaData.total_jornales_ejecutados,
          diaData.actividades.length
        ]);
      });

      const statsWorksheet = XLSX.utils.aoa_to_sheet(statsData);
      statsWorksheet['!cols'] = [
        { wch: 30 }, { wch: 15 }, { wch: 15 }, { wch: 15 }
      ];
      
      XLSX.utils.book_append_sheet(workbook, statsWorksheet, 'Estadísticas');

      // Generar nombre del archivo
      const nombreArchivo = `Plan_${MESES[plan.mes]}_${plan.año}_${CENTROS_PRODUCCION[plan.id_cp].replace(/\s+/g, '_')}_${moment().format('YYYY-MM-DD')}`;

      // Descargar el archivo
      XLSX.writeFile(workbook, `${nombreArchivo}.xlsx`);
      
      message.success(`Plan exportado exitosamente: ${nombreArchivo}.xlsx`);
      
    } catch (error) {
      console.error('Error al exportar plan mensual:', error);
      message.error('Error al exportar el plan mensual a Excel');
    }
  };

  return (
    <Button
      type="primary"
      icon={<FileExcelOutlined />}
      onClick={exportToExcel}
      disabled={!planData || !calendarioData}
    >
      Exportar Excel
    </Button>
  );
};

export default ExportPlanMensualExcel;

